import Vue from 'vue';
import Vuex from 'vuex';
import PubNub from 'pubnub';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { connect, createLocalVideoTrack } from 'twilio-video';

import User from '@/classes/User';
import Channel from '@/classes/Channel';
import Message from '@/classes/Message';

Vue.use(Vuex);

const MAX_API_FAILURES = 10;
const HISTORY_PAGE_SIZE = 20;

export default new Vuex.Store({
  state: {
    apiFailures: 0,
    isAdmin: false,
    chatEnabled: true,
    domainEnabled: true,
    digitalJoyDomains: ['engage.digitaljoy.media', 'chat.digitaljoy.media', 'engageqa.digitaljoy.media', 'chatqa.digitaljoy.media', 'engagepre.digitaljoy.media', 'chatpre.digitaljoy.media'],
    chatCallsEnabled: false,
    demoMode: false,
    adminTools: false,
    showingSignUp: false,
    showingSignUpCode: false,
    showingSignUpIncorrectCode: false,
    showingLogin: false,
    signUpName: '',
    signUpEmail: '',
    sidebarOpen: false,
    uuid: null,
    externalId: null,
    token: null,
    channelsInit: false,
    channelGroup: null,
    channelGroupConnected: false,
    channelGroupLoaded: false,
    chatApi: null,
    activeUser: null,
    activeChannel: null,
    activeMessageId: null,
    drawerOpen: false,
    hashVisualSettings: {},
    visualSettings: {},
    channels: [],
    users: [],
    activeUsers: [],
    recentUsers: [],
    typingInterval: undefined,
    typingAfkIntervals: {},
    ballotWSConnected: false,
    subscribers: [],
    clearedPins: [],
    deletedMessages: [],
    audioDevice: null,
    videoDevice: null,
    mediaDevices: null,
    callRoom: null,
    localVideoTrack:  null,
    callParticipants: [],
    callLocalTrack: null,
    callLocalRef: null,
    callRemoteRef: null,
    showingVideo: true,
    loadingVideo: false,
    demoUser: new User({
      uuid: 'demo',
      name: 'Sample User',
      email: 'sample@email.com',
      avatar: 'https://avatars.dicebear.com/api/male/sampleuser.svg?mood[]=happy',
      sponsor: true,
      active: true
    })
  },
  getters: {
    visualSettings(state) {
      return { ...state.visualSettings, ...state.hashVisualSettings };
    },
    hasCallParticipants(state) {
      return state.callParticipants.length > 0;
    },
    hasVideoOrAudioInput(state) {
      const videoInput = state.mediaDevices.find((device) => !device.label.toLowerCase().includes('obs') && device.kind === 'videoinput');
      const audioInput = state.mediaDevices.find((device) => device.kind === 'audioinput');
      return videoInput && videoInput.deviceId && audioInput && audioInput.deviceId;
    },
    videoConnectOptions: (state) => () => {
      const videoInput = state.mediaDevices.find((device) => !device.label.toLowerCase().includes('obs') && device.kind === 'videoinput');
      const audioInput = state.mediaDevices.find((device) => device.kind === 'audioinput');
      return {
        // logLevel: 'debug',
        bandwidthProfile: {
          video: {
            dominantSpeakerPriority: 'high',
            mode: 'collaboration',
            clientTrackSwitchOffControl: 'auto',
            contentPreferencesMode: 'auto'
          }
        },
        dominantSpeaker: true,
        maxAudioBitrate: 16000,
        preferredVideoCodecs: [{
          codec: 'VP8',
          simulcast: true
        }],
        video: (videoInput && videoInput.deviceId) ? {
          height: 720,
          frameRate: 24,
          width: 1280,
          deviceId: {
            exact: videoInput.deviceId
          }
        } : false,
        audio: (audioInput && audioInput.deviceId) ? {
          deviceId: {
            exact: audioInput.deviceId
          }
        } : false,
        name: (state.activeChannel || {}).id
      }
    },
    groupChannels(state) {
      return state.channels
        .filter((channel) => channel.type === 'group')
        .sort((a, b) => {
          if ((a.name || '').toLowerCase() > (b.name || '').toLowerCase()) {
            return 1;
          }
          if ((a.name || '').toLowerCase() < (b.name || '').toLowerCase()) {
            return -1;
          }
          return 0;
        });
    },
    chatterId(state) {
      return `${state.channelGroup}_traffic`;
    },
    directChannelId: (state) => (channelId) => {
      return [state.uuid, channelId].sort().join('_');
    },
    userById: (state) => (userUuid) => {
      return state.users.find(({ uuid }) => uuid === userUuid);
    },
    userByExternalId: (state) => (userExternalId) => {
      return state.users.find(({ externalId }) => externalId === userExternalId);
    },
    channelById: (state) => (channelId) => {
      return state.channels.find(({ id }) => id === channelId);
    },
    messageById: (state) => ({ channelId, messageId }) => {
      const channel = state.channels.find(({ id }) => id === channelId);
      const message = channel.messages.find(({ id }) => id === messageId);
      return message;
    },
    messageActionsById: (state) => ({ channelId, messageId }) => {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        return [...channel.actions].filter((action) => action.messageId === messageId);
      }
      return (channel || {}).actions || [];
    },
    chatCallsEnabled: ({ chatCallsEnabled }) => {
      return chatCallsEnabled;
    }
  },
  mutations: {
    SOCKET_ONOPEN (state) {
      console.log('ws open');
      state.ballotWSConnected = true;
    },
    SOCKET_ONCLOSE (state) {
      console.log('ws closed');
      state.ballotWSConnected = false;
    },
    SOCKET_ONERROR (state, event)  {
      console.error('WSS error', event);
    },
    SOCKET_RECONNECT() {},
    countApiFailure(state) {
      state.apiFailures++;
      return state.apiFailures < MAX_API_FAILURES;
    },
    setShowingVideo(state, showing) {
      state.showingVideo = showing;
    },
    setLoadingVideo(state, loading) {
      state.loadingVideo = loading;
    },
    setCallParticipants(state, participants) {
      state.callParticipants = participants;
    },
    setVideoToken(state, token) {
      state.videoToken = token;
    },
    setMediaDevices(state, devices) {
      state.mediaDevices = devices;
    },
    setCallRoom(state, callRoom) {
      state.callRoom = callRoom;
    },
    setLocalVideoTrack(state, localVideoTrack) {
      state.localVideoTrack = localVideoTrack;
    },
    setCallLocalTrack(state, callLocalTrack) {
      state.callLocalTrack = callLocalTrack;
    },
    setCallLocalRef(state, callLocalRef) {
      state.callLocalRef = callLocalRef;
    },
    setCallRemoteRef(state, callRemoteRef) {
      state.callRemoteRef = callRemoteRef;
    },
    setChatEnabled(state, enabled) {
      state.chatEnabled = enabled;
    },
    setDomainEnabled(state, enabled) {
      state.domainEnabled = enabled;
    },
    setChatCallsEnabled(state, enabled) {
      state.chatCallsEnabled = enabled;
    },
    setDemoMode(state, enabled) {
      state.demoMode = enabled;
    },
    initLocalStorageData(state) {
      try {
        state.uuid = localStorage.getItem('uuid');
        state.token = localStorage.getItem('token');
        state.audioDevice = localStorage.getItem('audioDeviceId');
        state.videoDevice = localStorage.getItem('videoDeviceId');
        state.clearedPins = JSON.parse(localStorage.getItem('clearedPins') || "[]");
        state.deletedMessages = JSON.parse(localStorage.getItem('deletedMessages') || "[]");
      } catch (e) {
        console.log('Unable to load local storage', e);
      }
    },
    setUuid(state, uuid) {
      state.uuid = uuid;
      if (!state.demoMode) {
        try {
          localStorage.setItem('uuid', uuid);
        } catch (e) {
          console.log('Unable to set uuid', e);
        }
      }
    },
    setExternalId(state, externalId) {
      state.externalId = externalId;
    },
    setHashVisualSettings(state) {
      try {
        state.hashVisualSettings = JSON.parse(decodeURIComponent(window.location.hash.substring(1)));
      } catch(e) {
        state.hashVisualSettings = {};
      }
    },
    setVisualSettings(state, settings) {
      state.visualSettings = settings;
    },
    setActiveUser(state, user) {
      state.activeUser = user;
    },
    async sortUsers(state) {
      const [recentUsers, activeUsers] = await Promise.all([
        this._vm.$worker.run((state) => {
          return state.users
            .filter(({ name, channelId }) => {
              const channel = state.channels.find(({ id }) => id === channelId);
              return name && channel && ((channel.messages || []).length || channel.notificationCount);
            })
            .filter(({ uuid }) => state.demoMode || uuid !== 'demo')
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        }, [{ users: state.users, channels: state.channels, demoMode: state.demoMode }]),
        this._vm.$worker.run((state) => {
          return state.users
            .filter(({ active, sponsor }) => state.users.length > 150 ? active || sponsor : true)
            .filter(({ uuid }) => state.demoMode || uuid !== 'demo')
            .filter(({ channelId }) => {
              const channel = state.channels.find(({ id }) => id === channelId);
              return !(channel && ((channel.messages || []).length || channel.notificationCount));
            })
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .sort((a, b) => a.sponsor === b.sponsor ? 0 : a.sponsor ? -1 : 1)
            .sort((a, b) => a.active === b.active ? 0 : a.active ? -1 : 1);
        }, [{ users: state.users, channels: state.channels, demoMode: state.demoMode }])
      ]);

      state.recentUsers = recentUsers;
      state.activeUsers = activeUsers;
    },
    setUsers(state, users) {
      state.users = users;
    },
    addSubscribers(state, subscribers) {
      state.subscribers = [...state.subscribers, ...subscribers];
    },
    removeSubscriber(state, subscriber) {
      state.subscribers = state.subscribers.filter((channel) => channel !== subscriber);
    },
    addUser(state, { uuid, name, email, avatar, sponsor, bio, phone, facebookUrl, title }) {
      const user = state.users.find((user) => user.uuid === uuid);
      !user && state.users.push(new User({
        uuid,
        name,
        email,
        avatar,
        sponsor,
        bio,
        phone,
        facebookUrl,
        title
      }));
    },
    updateUser(state, { uuid, name, email, avatar, sponsor, bio, phone, facebookUrl, title }) {
      const user = state.users.find((user) => user.uuid === uuid);
      if (user) {
        user.name = name;
        user.email = email;
        user.avatar = avatar;
        user.sponsor = sponsor;
        user.bio = bio;
        user.phone = phone;
        user.facebookUrl = facebookUrl;
        user.title = title;
      }
    },
    setUserActive(state, uuid) {
      const user = state.users.find((user) => user.uuid === uuid);
      if (user) {
        user.active = true;
      }
    },
    setUserInactive(state, uuid) {
      const user = state.users.find((user) => user.uuid === uuid);
      if (user) {
        user.active = false;
      }
    },
    setChannelGroup(state, channelGroup) {
      state.channelGroup = channelGroup;
    },
    setChannelGroupConnected(state, isConnected) {
      state.channelGroupConnected = isConnected;
    },
    setChannelGroupLoaded(state, isLoaded) {
      state.channelGroupLoaded = isLoaded
    },
    setChatApi(state, chatApi) {
      state.chatApi = chatApi;
    },
    setChannels(state, channels) {
      state.channels = channels;
      state.channelsInit = true;
    },
    addChannel(state, channel) {
      state.channels.push(channel);
    },
    updateChannel(state, { id, name, avatar, banner, description }) {
      const channel = state.channels.find((channel) => channel.id === id);
      if (channel) {
        channel.name = name;
        channel.avatar = avatar;
        channel.banner = banner;
        channel.description = description;
      }
    },
    removeChannel(state, channelId) {
      const index = state.channels.findIndex(({ id }) => id === channelId);
      index !== -1 && state.channels.splice(index, 1);
    },
    removeUser(state, uuid) {
      const index = state.users.findIndex((user) => (user || {}).uuid === uuid);
      index !== -1 && state.users.splice(index, 1);
    },
    setUserChannelById(state, { uuid, channel }) {
      const user = state.users.find((user) => user.uuid === uuid);
      user.channelId = channel.id;
    },
    setActiveChannel(state, channel) {
      state.activeChannel = channel;
      if (state.activeChannel) {
        state.activeChannel.notificationCount = 0;
        state.activeChannel.sticky = true;
      }
      state.sidebarOpen = false;
      state.drawerOpen = false;
    },
    setActiveMessageId(state, messageId) {
      state.activeMessageId = messageId;
      state.drawerOpen = !!messageId;
    },
    setDrawerOpen(state, isOpen) {
      state.drawerOpen = isOpen;
    },
    setChannelError(state, channelId) {
      const channel = state.channels.find(({ id }) => id === channelId);
      channel.error = true;
    },
    addChannelMessageActions(state, { channelId, actions, nextTimeToken }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        channel.actions = [...channel.actions, ...actions];

        if (nextTimeToken) {
          channel.actionNextTimeToken = nextTimeToken;
        }
      }
    },
    removeChannelMessageAction(state, { channelId, actionId }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        const index = channel.actions.findIndex((action) => action.actionId === actionId);
        index !== -1 && channel.actions.splice(index, 1);
      }
    },
    addChannelMessageHistory(state, { channelId, nextTimeToken, messages }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        channel.messages = [...messages, ...channel.messages];
        channel.init = true;
        channel.error = false;
        channel.nextTimeToken = nextTimeToken;
        channel.doneFetching = (messages.length < HISTORY_PAGE_SIZE);
      }
    },
    resetChannelMessageHistory(state, channelId) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        channel.messages = [];
        channel.actions = [];
      }
    },
    setChannelSticky(state, { channelId, isSticky }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        channel.sticky = isSticky;
        channel.notificationCount = 0;
      }
    },
    setChannelSubscribed(state, channelId) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        channel.subscribed = true;
      }
    },
    // startTyping(state, func) {
    //   func();
    //   state.typingInterval = setInterval(func, 5000);
    // },
    // stopTyping(state) {
    //   clearInterval(state.typingInterval);
    //   state.typingInterval = undefined;
    // },
    incrementChannelNotification(state, { channelId, senderUuid }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (
        (
          channelId !== state.activeChannel.id
          && senderUuid !== state.activeUser.uuid
        )
        || (
          channelId === state.activeChannel.id
          && !state.activeChannel.sticky
        )
      ) {
        channel.notificationCount++;
      }
    },
    removeMessageFromChannel(state, { channelId, timeToken }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      channel.messages = channel.messages.filter((message) => message.id !== timeToken);
    },
    // filterTypingUsers(state, channelId) {
    //   const channel = state.channels.find(({ id }) => id === channelId);
    //   channel.typingUsers = channel.typingUsers.filter((user) => user);
    // },
    // addTypingUserToChannel(state, { channelId, user }) {
    //   const channel = state.channels.find(({ id }) => id === channelId);
    //   !channel.typingUsers
    //     .filter((typingUser) => typingUser)
    //     .some(({ uuid }) => uuid === user.uuid) && channel.typingUsers.push(user);
    // },
    // removeTypingUserFromChannel(state, { channelId, userUuid }) {
    //   const channel = state.channels.find(({ id }) => id === channelId);
    //   const userIndex = channel.typingUsers.findIndex(({ uuid }) => uuid === userUuid);
    //   userIndex !== -1 && channel.typingUsers.splice(userIndex, 1);
    // },
    setSidebarOpen(state, isOpen) {
      state.sidebarOpen = isOpen;
    },
    showSignUp(state, isShowing) {
      state.showingSignUp = isShowing;
    },
    showLogin(state, isShowing) {
      state.showingLogin = isShowing;
    },
    setAdmin(state, value) {
      state.isAdmin = value;
    },
    showSignUpCode(state, showing) {
      state.showingSignUpCode = showing;
    },
    showSignUpIncorrectCode(state, showing) {
      state.showingSignUpIncorrectCode = showing;
    },
    setSignUpName(state, name) {
      state.signUpName = name;
    },
    setSignUpEmail(state, email) {
      state.signUpEmail = email;
    },
    pinChannelMessage(state, { channelId, messageId, value }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (channel) {
        const message = channel.messages.find((message) => message.id === messageId);
        (message || {}).pinned = value;
      }
    },
    clearPin(state, actionId) {
      state.clearedPins.push(actionId);
      try {
        localStorage.setItem('clearedPins', JSON.stringify(state.clearedPins));
      } catch (e) {
        console.log('Unable to set cleared pins', e);
      }
    },
    addToDeleteMessageStore(state, timeToken) {
      state.deletedMessages.push(timeToken);
      try {
        localStorage.setItem('deletedMessages', JSON.stringify(state.deletedMessages));
      } catch (e) {
        console.log('Unable to set deleted messages', e);
      }
    }
  },
  actions: {
    sleep(obj, ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    addMessageToChannel({ state, commit }, { channelId, message }) {
      const channel = state.channels.find(({ id }) => id === channelId);
      if (!channel.messages.find(({ id }) => id === message.id)) {
        channel.messages.push(message);
        commit('incrementChannelNotification', {
          channelId,
          senderUuid: message.sender.uuid
        });
        // if (channel.messages.length > 1000) {
        //   channel.messages = channel.messages.slice(channel.messages.length - 1000);
        // }
      }
    },
    chatSettings({ commit }, { action, payload }) {
      switch(action) {
        case 'chatSettings':
          switch(payload.action) {
            case 'update':
              commit('setVisualSettings', payload.settings)
          }
      }
    },
    resetChatMessages({ getters, commit }, { action, payload }) {
      switch(action) {
        case 'resetChatMessages':
          switch(payload.action) {
            case 'resetMessages':
              payload.channels.forEach((channel) => {
                getters.channelById(channel) && commit('resetChannelMessageHistory', channel);
              });
              break;
            case 'removeMessage': {
              const {
                channelId,
                timeToken
              } = payload;
              commit('removeMessageFromChannel', { channelId, timeToken });
              commit('addToDeleteMessageStore', timeToken);
              break;
            }
          }
          break;
      }
    },
    chatChannel({ state, dispatch, commit }, { action, payload }) {
      switch(action) {
        case 'chatChannel':
          switch(payload.action) {
            case 'removed':
              payload.channelGroup === state.channelGroup && dispatch('removeChannel', payload.channelId);
              break;
            case 'added':
              payload.channelGroup === state.channelGroup && dispatch('addChannel', new Channel({
                id: payload.channelId,
                name: payload.name,
                avatar: payload.avatar,
                banner: payload.banner,
                description: payload.description,
                type: 'group'
              }));
              break;
            case 'updated':
              commit('updateChannel', new Channel({
                id: payload.channelId,
                name: payload.name,
                avatar: payload.avatar,
                banner: payload.banner,
                description: payload.description,
                type: 'group'
              }));
              break;
          }
          break;
      }
    },
    chatUser({ state, dispatch, getters, commit }, { payload }) {
      if (payload.channelGroup !== state.accountName) {
        return;
      }
      switch(payload.action) {
        case 'remove':
          if(getters.userById(payload.uuid)) {
            dispatch('removeUser', getters.userById(payload.uuid));
            (state.uuid === payload.uuid) && commit('showSignUp', true);
          }
          break;
        case 'create':
        case 'update':
          if (getters.userById(payload.uuid)) {
            commit('updateUser', {
              uuid: payload.uuid,
              name: payload.name,
              email: payload.email,
              externalId: payload.externalId,
              avatar: payload.avatar,
              sponsor: payload.sponsor,
              bio: payload.bio,
              title: payload.title,
              phone: payload.phone,
              facebookUrl: payload.facebookUrl
            });
          } else {
            commit('addUser', {
              uuid: payload.uuid,
              name: payload.name,
              email: payload.email,
              externalId: payload.externalId,
              avatar: payload.avatar,
              sponsor: payload.sponsor,
              bio: payload.bio,
              title: payload.title,
              phone: payload.phone,
              facebookUrl: payload.facebookUrl
            });
          }

          const channel = new Channel({
            id: getters.directChannelId(payload.uuid),
            name: payload.name,
            type: 'direct'
          });
          commit('addChannel', channel);
          commit('setUserChannelById', { uuid: payload.uuid, channel });
          break;
      }
    },
    // startTyping({ state, commit }) {
    //   !state.typingInterval && !state.demoMode && commit('startTyping', () => {
    //     state.chatApi.signal({
    //       message: 'typing_on',
    //       channel: state.activeChannel.id
    //     });
    //   });
    // },
    // stopTyping({ state, commit }) {
    //   if (state.typingInterval) {
    //     state.chatApi.signal({
    //       message: 'typing_off',
    //       channel: state.activeChannel.id
    //     });
    //     commit('stopTyping');
    //   }
    // },
    clearPin({ commit }, actionId) {
      commit('clearPin', actionId);
    },
    setActiveChannelById({ dispatch, getters }, channelId) {
      const channel = getters.channelById(channelId);
      if (channel) {
        dispatch('setActiveChannel', channel);
      }
    },
    setActiveChannel({ commit, dispatch }, channel) {
      commit('setActiveChannel', channel);
      if (channel && !channel.init) {
        dispatch('addChannelMessagesPage', channel.id);
        dispatch('addChannelMessageActionsPage', channel.id);
        dispatch('initChannels', [channel.id]);
      }
    },
    setChannelGroup({ commit }, channelGroup) {
      commit('setChannelGroup', channelGroup);
    },
    sendMessage({ commit, state, getters, dispatch }, { message, attachment, isCall = false, isNotification = false }) {
      if (state.demoMode) {
        return dispatch('addMessageToChannel', {
          channelId: state.activeChannel.id,
          message: new Message({
            id: Date.now() * 1000,
            text: message,
            channelId: state.activeChannel.id,
            sender: getters.userById(state.demoUser.uuid)
          })
        });
      }
      if (attachment) {
        state.chatApi.sendFile({
          channel: state.activeChannel.id,
          file: attachment,
          message: {
            text: message
          },
          meta: {
            senderUuid: state.activeUser.uuid,
            channelGroup: state.channelGroup
          }
        },
          async (status) => {
            if (!status.error) {
              await dispatch('sendMessageNotification', { message });
            }
          }
        );
      } else {
        state.chatApi.publish({
          channel: state.activeChannel.id,
          message: {
            text: message
          },
          meta: {
            isNotification,
            isCall,
            senderUuid: state.activeUser.uuid,
            channelGroup: state.channelGroup
          }
        },
          async (status) => {
            if (!status.error) {
              await dispatch('sendMessageNotification', { message });
            }
          }
        );
      }

      //traffic message
      state.activeChannel.type !== 'group' && state.chatApi.publish({
        channel: getters.chatterId,
        message: {
          text: (state.users.find((user) => user.channelId === state.activeChannel.id) || {}).uuid
        },
        meta: {
          senderUuid: state.activeUser.uuid,
          isNotification,
          isCall
        }
      });

      commit('setChannelSticky', {
        channelId: state.activeChannel.id,
        isSticky: true
      });

      process.env.VUE_APP_ENVIRONMENT !== 'local' &&
        Vue.prototype.$gtag.event('sent_message', {});
      // dispatch('stopTyping');
    },
    async initVideo({ commit, state }) {
      try {
        const { data: token } = await axios.get(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/video/token?identity=${(state.activeUser || {}).uuid}`);
        commit('setVideoToken', token);
      } catch (error) {
        console.log("Unable to get video token or user doesn't have this feature enabled");
      }
    },
    getTracks(obj, participant) {
      return Array.from(participant.tracks.values()).filter((publication) => {
        return publication.track;
      }).map((publication) => {
        return publication.track;
      });
    },
    attachTracks({ state }, publication) {
      publication.on('subscribed', track => {
        state.callRemoteRef.appendChild(track.attach());
      });

      publication.on('unsubscribed', track => {
        track.detach().forEach(function(element) {
          element.remove();
        });
      });
    },
    async detachTracks({ dispatch }, participant) {
      (await dispatch('getTracks', participant)).forEach((track) => {
        track.detach().forEach((detachedElement) => {
          detachedElement.remove();
        });
      });
    },
    attachParticipantTracks({ dispatch }, participant) {
      participant.tracks.forEach(publication => {
        dispatch('attachTracks', publication);
      });

      participant.on('trackPublished', publication => {
        dispatch('attachTracks', publication);
      });

      participant.on('trackUnpublished', (publication) => {
        console.log('trackUnpublished', publication);
      });
    },
    detachParticipantTracks({ dispatch }, participant) {
      let tracks = Array.from(participant.tracks.values());
      dispatch('detachTracks', tracks);
    },
    hangUp({ commit, state, dispatch }, { otherHungUp }) {
      if (state.callRoom) {
        state.callRoom.disconnect();
        commit('setCallLocalTrack', false);
        state.localVideoTrack.stop();
        state.callRoom.localParticipant.unpublishTrack(state.localVideoTrack);
        state.callLocalRef.innerHTML = '';

        !otherHungUp && dispatch('sendMessage', {
          message: `has ended the call`,
          isNotification: true
        });
      }
    },
    async loadMediaDevices({ commit }) {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        commit('setMediaDevices', await navigator.mediaDevices.enumerateDevices());
      } catch (e) {
        Vue.prototype.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio/Video Access',
          text: 'Unable to access audio or video devices to begin call. Please ensure a device is not in use.',
          duration: 5000
        });
      }
    },
    async showVideo({ state, commit, dispatch, getters }, { show }) {
      try {
        if (state.loadingVideo) {
          return;
        }
        commit('setShowingVideo', show);
        commit('setLoadingVideo', true);
        if (show) {
          commit('setLocalVideoTrack', await createLocalVideoTrack(getters.videoConnectOptions().video));
          await state.callRoom.localParticipant.publishTrack(state.localVideoTrack);
          state.callLocalRef.appendChild(state.localVideoTrack.attach());
        } else {
          (await dispatch('getTracks', state.callRoom.localParticipant)).forEach((track) => {
            if (track.kind === 'video') {
              state.localVideoTrack.stop();
              state.callRoom.localParticipant.unpublishTrack(state.localVideoTrack);
              state.callLocalRef.innerHTML = '';
            }
          });
        }
      } catch(e) {
        console.error(e);
      }
      commit('setLoadingVideo', false);
    },
    async initCall({ commit, state, dispatch, getters }, { video }) {
      try {
        if (state.callLocalTrack || !state.chatCallsEnabled || !state.videoToken) {
          return;
        }
        commit('setCallLocalTrack', true);
        video && commit('setShowingVideo', true);
        commit('setCallRoom', await connect(state.videoToken, getters.videoConnectOptions()));

        window.room = state.callRoom;

        dispatch('attachParticipantTracks', window.room.localParticipant);

        state.callRoom.participants.forEach((participant) => {
          console.log('participantPreconnected', participant);
          commit('setCallParticipants', [...state.callParticipants, participant]);
          dispatch('attachParticipantTracks', participant);
        });

        // When a Participant joins the Room, log the event.
        window.room.on('participantConnected', (participant) => {
          console.log('participantConnected', participant);
          commit('setCallParticipants', [...state.callParticipants, participant]);
          dispatch('attachParticipantTracks', participant);
        });

        // When a Participant leaves the Room, detach its Tracks.
        window.room.on('participantDisconnected', (participant) => {
          console.log('participantDisconnected', participant);
          commit('setCallParticipants', []);
          dispatch('detachParticipantTracks', participant);
          dispatch('hangUp', { otherHungUp: true });
        });

        commit('setLocalVideoTrack', Array.from(state.callRoom.localParticipant.videoTracks.values())[0].track);
        await state.callRoom.localParticipant.publishTrack(state.localVideoTrack);
        state.callLocalRef.appendChild(state.localVideoTrack.attach());

        !video && dispatch('showVideo', { show: video });

        dispatch('sendMessage', {
          message: state.callRoom.participants.length ? 'has answered the call' : `has initiated a call`,
          isCall: !state.callRoom.participants.length,
          isNotification: true
        });

        process.env.VUE_APP_ENVIRONMENT !== 'local' &&
          Vue.prototype.$gtag.event('entered_call', {});
      } catch (e) {
        console.log(e);
      }
    },
    async createAction({ state }, { messageId, channelId, type, value }) {
      await axios.post(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/channel/${channelId}/message/${messageId}/action`, {
        userUuid: state.activeUser.uuid,
        type,
        value
      });
    },
    async removeAction({ state }, { messageId, channelId, actionId }) {
      await axios.delete(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/channel/${channelId}/message/${messageId}/action/${actionId}`);
    },
    async createAdminAction({ state }, { messageId, channelId, type, value }) {
      await axios.post(`${process.env.VUE_APP_CDN_ORIGIN}${process.env.VUE_APP_API_ROOT}api/v1/user/me/chat/channel/${channelId}/message/${messageId}/action`, {
        type,
        value
      }, {
        headers: {
          Authorization: `Bearer ${state.token}`
        }
      });
    },
    async removeAdminAction({ state }, { messageId, channelId, actionId }) {
      await axios.delete(`${process.env.VUE_APP_CDN_ORIGIN}${process.env.VUE_APP_API_ROOT}api/v1/user/me/chat/channel/${channelId}/message/${messageId}/action/${actionId}`, {
        headers: {
          Authorization: `Bearer ${state.token}`
        }
      });
    },
    async removeMessage({ state }, { messageId, channelId }) {
      await axios.delete(`${process.env.VUE_APP_CDN_ORIGIN}${process.env.VUE_APP_API_ROOT}api/v1/user/me/chat/channel/${channelId}/message/${messageId}`, {
        headers: {
          Authorization: `Bearer ${state.token}`
        }
      });
    },
    async addChannelMessageActionsPage({ state, commit, getters, dispatch }, channelId) {
      try {
        const channel = getters.channelById(channelId);
        if (channel) {
          const {
            data: actions,
            start: nextTimeToken
          } = await state.chatApi.getMessageActions({
            channel: channelId,
            stringifiedTimeToken: true,
            start: channel.actionNextTimeToken,
            count: HISTORY_PAGE_SIZE
          });

          commit('addChannelMessageActions', {
            channelId,
            nextTimeToken,
            actions: actions.map((action) => {
              return {
                type: action.type,
                value: action.value,
                userUuid: action.uuid,
                actionId: action.actionTimetoken,
                messageId: action.messageTimetoken
              }
            })
          });

          if (actions.length > 0 && actions.length < 100) {
            await dispatch('addChannelMessageActionsPage', channelId);
          }
        }
      } catch (e) {
        Sentry.captureException(e);
        commit('setChannelError', channelId);
      }
    },
    async addChannelMessagesPage({ state, commit, getters }, channelId) {
      try {
        const channel = getters.channelById(channelId);
        if (channel) {
          const {
            messages,
            startTimeToken: nextTimeToken
          } = await state.chatApi.history({
            channel: channelId,
            includeMeta: true,
            stringifiedTimeToken: true,
            start: channel.nextTimeToken,
            count: HISTORY_PAGE_SIZE
          })

          commit('addChannelMessageHistory', {
            channelId,
            nextTimeToken,
            messages: messages
              .filter(({ meta }) => meta)
              .map(({ timetoken, entry, meta }) => {
                const message = new Message({
                  id: timetoken,
                  text: entry.text,
                  channelId: channelId,
                  isNotification: meta.isNotification,
                  isCall: meta.isCall,
                  sender: getters.userById(meta.senderUuid) || {
                    uuid: meta.senderUuid,
                    name: 'Unknown'
                  }
                });
                if (entry.file) {
                  message.text = entry.message.text;
                  message.attachment = state.chatApi.getFileUrl({
                    channel: channelId,
                    id: entry.file.id,
                    name: entry.file.name
                  });
                }
                return message;
              })
          });
        }
      } catch (e) {
        Sentry.captureException(e);
        commit('setChannelError', channelId);
      }
    },
    initChannels({ state, commit }, channels) {
      channels = channels.filter((channel) => !state.subscribers.includes(channel));
      channels.length && commit('addSubscribers', channels);
      channels.length && state.chatApi.subscribe({
        channels: channels,
        withPresence: true
      });
    },
    addChannel({ dispatch, commit }, channel) {
      dispatch('initChannels', [channel.id]);
      commit('addChannel', channel);
    },
    removeChannel({ state, commit, dispatch, getters }, channelId) {
      state.chatApi.unsubscribe({
        channels: [channelId],
        withPresence: true
      });
      commit('removeChannel', channelId);
      commit('removeSubscriber', channelId);
      channelId === (state.activeChannel || {}).id && dispatch('setActiveChannel', getters.groupChannels[0]);
    },
    async initGroupChannels({ state, dispatch, getters }) {
      const channels = state.channels
      .filter((channel) =>
        channel.type === 'group'
      )
      .map((channel) =>
        channel.id
      );
      dispatch('initChannels', channels);
      getters.groupChannels.length && dispatch('setActiveChannel', getters.groupChannels[0]);
    },
    async loginUser({ state, commit, dispatch, getters }, { name, email, avatar, bio, phone, facebookUrl, title }) {
      let user;
      if (state.externalId) {
        user = getters.userByExternalId(state.externalId);
        user && commit('setUuid', user.uuid);
      }
      if (state.uuid || state.demoMode) {
        user = getters.userById(state.demoMode ? state.demoUser.uuid : state.uuid);
        if (user) {
          commit('setUuid', user.uuid);
          if ((name || email || avatar || phone || bio || title || facebookUrl) && !state.demoMode) {
            try {
              await axios.put(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/guest/${user.uuid}`, {
                name,
                email,
                avatar,
                bio,
                phone,
                facebookUrl,
                title
              });
            } catch (e) {
              Sentry.captureException(e);
              console.log('unable to update user');
            }
          }
          commit('showLogin', false);
          return dispatch('initChat');
        }
      }

      if (name) {
        return dispatch('signUpUser', { name, email, avatar, bio, phone, facebookUrl, title });
      } else if (!state.visualSettings.publicMode) {
        return commit('showLogin', true);
      } else {
        return commit('showSignUp', true);
      }
    },
    async signUpUser({ state, commit, dispatch, getters }, { name, email, avatar, bio, phone, facebookUrl, title, code }) {
      try {
        const { data } = await axios.post(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/guest`, {
          name,
          email,
          avatar,
          bio,
          phone,
          facebookUrl,
          title,
          externalId: state.externalId,
          code
        });
        commit('showSignUp', false);
        commit('setUuid', data.uuid);
        !getters.userById(data.uuid) && commit('addUser', {
          uuid: data.uuid
        });
        await dispatch('loginUser', {});
        commit('setSignUpName', '');
        commit('setSignUpEmail', '');
        commit('showSignUpCode', false);
      } catch (e) {
        commit('setSignUpName', name);
        commit('setSignUpEmail', email);
        commit('showSignUp', true);
        switch((((e || {}).response || {}).data || {}).id) {
          case 'CODE_REQUIRED':
            commit('showSignUpCode', true);
            break;
          case 'CODE_INCORRECT':
            commit('showSignUpIncorrectCode', true);
            break;
          default:
            Sentry.captureException(e);
        }
      }
    },
    async verifyAdmin({ state, commit }) {
      if (!state.token) {
        commit('setAdmin', false);
        return;
      }
      try {
        const {
          data: {
            accountName
          }
        } = await axios.get(`${process.env.VUE_APP_CDN_ORIGIN}${process.env.VUE_APP_API_ROOT}api/v1/user/me`, {
          headers: {
            Authorization: `Bearer ${state.token}`
          }
        });
        if (state.channelGroup === accountName) {
          commit('setAdmin', true);
        }
      } catch(e) {
        console.log('not an admin');
      }
    },
    initActiveUser({ getters, state, commit }) {
      commit('setActiveUser', getters.userById(state.uuid));
    },
    async loadAccountFeatures({ commit, state }) {
      try {
        const { data: features } = await axios.get(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/features`);
        commit('setChatEnabled', features.chat);
        commit('setChatCallsEnabled', features.chatCallsEnabled);
      } catch (e) {
        Sentry.captureException(e);
        commit('setChatEnabled', false);
        commit('setChatCallsEnabled', false);
      }
    },
    async loadSettings({ commit, state, dispatch }) {
      try {
        const { data: settings } = await axios.get(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/settings`);
        const ancestorOrigin = window.location.ancestorOrigins && window.location.ancestorOrigins.length === 1 && window.location.ancestorOrigins[0];
        if (settings.enableProtectedDomains) {

          if(!ancestorOrigin) {
            commit('setDomainEnabled', false);
            return;
          }

          const isDigitalJoyDomain = state.digitalJoyDomains.some((domain) => {
            return ancestorOrigin.includes(domain);
          });

          if (isDigitalJoyDomain) {
            commit('setDomainEnabled', true);
            return;
          }

          const isProtectedDomain = settings.protectedDomains.some((domain) => {
            return ancestorOrigin.includes(domain);
          });

          if (!isProtectedDomain) {
            commit('setDomainEnabled', false);
            return;
          }
        }

        commit('setVisualSettings', settings);
      } catch (e) {
        Sentry.captureException(e);
        if (!commit('countApiFailure')) {
          return;
        }
        await dispatch('sleep', 3000);
        await dispatch('loadSettings');
      }
    },
    async loadChannels({ commit, state, dispatch }) {
      try {
        const { data: channels } = await axios.get(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/channels`);
        commit('setChannels', channels.map(({ id, name, avatar, banner, description }) => {
          return new Channel({
            id,
            name,
            avatar,
            banner,
            description,
            type: 'group'
          });
        }));
      } catch (e) {
        Sentry.captureException(e);
        if (!commit('countApiFailure')) {
          return;
        }
        await dispatch('sleep', 3000);
        await dispatch('loadChannels');
      }
    },
    async loadUsers({ dispatch, commit, state }) {
      try {
        const { data: users } = await axios.get(`${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/user/${state.channelGroup}/chat/guests`)
        commit('setUsers', users.map(({ userId: uuid, name, email, externalId, avatar, sponsor, phone, title, bio, facebookUrl }) => {
          return new User({
            uuid,
            name,
            email,
            externalId,
            avatar,
            sponsor,
            phone,
            title,
            bio,
            facebookUrl
          });
        }));
        state.demoMode && commit('addUser', state.demoUser);
      } catch (e) {
        Sentry.captureException(e);
        if (!commit('countApiFailure')) {
          return;
        }
        await dispatch('sleep', 3000);
        await dispatch('loadUsers');
      }
    },
    removeUser({ commit, dispatch }, { channelId, uuid }) {
      dispatch('removeChannel', channelId);
      commit('removeUser', uuid);
    },
    async initUsers({ state, commit, getters }) {
      const { channels } = await state.chatApi.hereNow({
        channelGroups: [state.channelGroup],
        includeUUIDs: true
      });
      channels && Object.keys(channels).length && channels[Object.keys(channels)[0]].occupants.forEach(({ uuid }) => {
        commit('setUserActive', uuid);
      });
      state.users.map(({ uuid, name }) => {
        const channel = new Channel({
          id: getters.directChannelId(uuid),
          name,
          type: 'direct'
        });
        commit('addChannel', channel);
        commit('setUserChannelById', { uuid, channel });
        return channel.id;
      });
    },
    processAction({ commit }, { channelId, data }) {
      switch(data.type) {
        case 'pin':
          commit('pinChannelMessage', {
            channelId,
            messageId: data.messageTimetoken,
            value: data.value === '1'
          });
          break;
      }
    },
    initChatListeners({ commit, getters, dispatch, state }) {
      state.chatApi.addListener({
        message: (e) => {
          try {
            if (e.channel === getters.chatterId) {
              const senderDirectChannelId = getters.directChannelId(e.publisher);
              const senderChannel = getters.channelById(senderDirectChannelId);
              if (e.message.text === state.uuid && senderChannel && !senderChannel.subscribed) {
                dispatch('initChannels', [senderDirectChannelId]);
                dispatch('addChannelMessagesPage', senderDirectChannelId);
                commit('incrementChannelNotification', {
                  channelId: senderDirectChannelId,
                  senderUuid: e.publisher
                });

                if (e.userMetadata.isCall && e.publisher !== state.uuid) {
                  Vue.prototype.$notify({
                    group: 'app',
                    type: 'success',
                    title: 'Incoming Call',
                    text: `You are recieving a call from ${(getters.userById(e.publisher) || {}).name}`,
                    duration: 5000
                  });
                }
              }
            } else {
              !state.deletedMessages.includes(e.timetoken) && dispatch('addMessageToChannel', {
                channelId: e.channel,
                message: new Message({
                  id: e.timetoken,
                  text: e.message.text,
                  channelId: e.channel,
                  isNotification: e.userMetadata.isNotification,
                  isCall: e.userMetadata.isCall,
                  sender: getters.userById(e.publisher) || {
                    uuid: e.publisher
                  }
                })
              });
            }
          } catch (e) {
            console.log(e);
            Sentry.captureException(e);
          }
        },
        presence: (e) => {
          try {
            switch(e.action) {
              case 'join':
                commit('addUser', {
                  uuid: e.uuid
                });
                commit('setUserActive', e.uuid);
                break;
              case 'leave':
              case 'timeout':
                commit('setUserInactive', e.uuid);
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        },
        // signal: (e) => {
        //   try {
        //     const typingId = `${e.channel}_${e.publisher}`;
        //     commit('filterTypingUsers', e.channel);
        //     switch(e.message) {
        //       case 'typing_on':
        //         commit('addTypingUserToChannel', {
        //           channelId: e.channel,
        //           user: getters.userById(e.publisher)
        //         });
        //         clearTimeout(state.typingAfkIntervals[typingId]);
        //         state.typingAfkIntervals[typingId] = setTimeout(() => {
        //           commit('removeTypingUserFromChannel', {
        //             channelId: e.channel,
        //             userUuid: e.publisher
        //           });
        //         }, 10000);
        //         break;
        //       case 'typing_off':
        //         commit('removeTypingUserFromChannel', {
        //           channelId: e.channel,
        //           userUuid: e.publisher
        //         });
        //         break;
        //     }
        //   } catch (e) {
        //     Sentry.captureException(e);
        //   }
        // },
        file: (e) => {
          try {
            dispatch('addMessageToChannel', {
              channelId: e.channel,
              message: new Message({
                id: e.timetoken,
                text: e.message.text,
                channelId: e.channel,
                attachment: e.file.url,
                sender: getters.userById(e.publisher) || {
                  uuid: e.publisher
                }
              })
            });
          } catch (e) {
            Sentry.captureException(e);
          }
        },
        messageAction: (e) => {
          try {
            console.log(e);
            switch(e.event) {
              case 'added':
                commit('addChannelMessageActions', {
                  channelId: e.channel,
                  actions: [{
                    type: e.data.type,
                    value: e.data.value,
                    userUuid: e.data.uuid,
                    actionId: e.data.actionTimetoken,
                    messageId: e.data.messageTimetoken
                  }]
                });
                break;
              case 'removed':
                commit('removeChannelMessageAction', {
                  channelId: e.channel,
                  actionId: e.data.actionTimetoken
                });

                break;
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        },
        status: (e) => {
          try {
            if (e.category === 'PNConnectedCategory') {
              if (e.operation === 'PNSubscribeOperation') {
                e.affectedChannels.forEach((channelId) => {
                  commit('setChannelSubscribed', channelId);
                });
              }
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        }
      });
    },
    initChatApi({ state, commit }) {
      const chatApi = new PubNub({
        publishKey: process.env.VUE_APP_PUBNUB_PUBLISH_KEY,
        subscribeKey: process.env.VUE_APP_PUBNUB_SUBSCRIBE_KEY,
        uuid: state.uuid
      });
      commit('setChatApi', chatApi);
    },
    async initChat({ dispatch, getters }) {
      dispatch('initActiveUser');
      dispatch('initChatApi');
      dispatch('initChatListeners');
      dispatch('initChannels', [getters.chatterId]);
      await dispatch('initUsers');
      dispatch('initGroupChannels');
      dispatch('initVideo');
      dispatch('verifyAdmin');

      process.env.VUE_APP_ENVIRONMENT !== 'local' &&
        Vue.prototype.$gtag.event('joined_chat', {});
    },
    async sendMessageNotification({ state }, { message }) {
      await axios.post(
        `${process.env.VUE_APP_AWS_SERVERLESS_URL}/api/notifications/chats/${state.channelGroup}/channels/${state.activeChannel.id}`,
        {
          message,
          senderId: state.activeUser.uuid
        }
      );
    }
  }
});
